import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Star } from "lucide-react";

/**
 * True blocking modal — no close button. Shown whenever the backend flags
 * `user.rating_due` (every 5th login, for every non-admin account). Covers
 * the whole app until 1-5 stars is submitted.
 */
export default function AppRatingGate() {
  const { user, setUser } = useAuth();
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);

  if (!user || !user.rating_due) return null;

  const submit = async () => {
    if (!stars) return;
    setBusy(true);
    try {
      await api.post("/app-rating", { stars });
      toast.success("Thanks for rating Counseltaria!");
      setUser(prev => (prev ? { ...prev, rating_due: false } : prev));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="app-rating-gate" className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(20,20,18,0.75)" }}>
      <div className="card-soft w-full max-w-sm p-8 text-center">
        <div className="font-serif-display text-2xl mb-2">Enjoying Counseltaria™?</div>
        <div className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
          Rate your experience so far — it takes two seconds.
        </div>
        <div className="flex items-center justify-center gap-2 mb-7" data-testid="app-rating-stars">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" data-testid={`app-rating-star-${n}`}
              onClick={() => setStars(n)}
              onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
              aria-label={`${n} star${n > 1 ? "s" : ""}`}>
              <Star className="w-9 h-9 transition-colors"
                style={{
                  color: (hover || stars) >= n ? "var(--brand)" : "var(--border-soft)",
                  fill: (hover || stars) >= n ? "var(--brand)" : "transparent",
                }} />
            </button>
          ))}
        </div>
        <button data-testid="app-rating-submit" onClick={submit} disabled={!stars || busy}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed">
          {busy ? "Submitting…" : "Submit rating"}
        </button>
      </div>
    </div>
  );
}
